import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
    IconActivity, IconTrendingUp, IconAlertTriangle, IconRefresh, IconArrowUp, IconArrowDown,
    IconChartBar
} from '../components/Icons';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    AreaChart, Area, ReferenceLine, ComposedChart, Bar
} from 'recharts';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../config';

const CACHE_KEY = 'aiq_market_breadth_data';
const CACHE_TIME_KEY = 'aiq_market_breadth_time';

// Load persisted data immediately (before first render)
const loadCachedBreadth = () => {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        const ts  = localStorage.getItem(CACHE_TIME_KEY);
        if (raw) return { data: JSON.parse(raw), ts: ts ? new Date(parseInt(ts)) : null };
    } catch {}
    return null;
};

const MarketBreadthView: React.FC = () => {
    const { currentUser } = useAuth();
    const cached = loadCachedBreadth();
    const [data, setData] = useState<any>(cached?.data ?? null);
    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(cached?.ts ?? null);
    const [days, setDays] = useState(180);

    const fetchBreadthData = async (newDays?: number) => {
        if (!currentUser) return;
        const queryDays = newDays ?? days;
        setIsLoading(true);
        toast.loading("Syncing Market Breadth...", { id: 'breadth-refresh', duration: 3000 });

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/macro/market_breadth?days=${queryDays}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error("Failed to load market breadth data");
            const json = await res.json();
            const now = Date.now();
            setData(json);
            setLastUpdated(new Date(now));
            localStorage.setItem(CACHE_KEY, JSON.stringify(json));
            localStorage.setItem(CACHE_TIME_KEY, now.toString());
            toast.success("Market Breadth updated", { id: 'breadth-refresh' });
        } catch (e) {
            console.error(e);
            toast.error("Could not refresh. Showing last saved data.", { id: 'breadth-refresh' });
        } finally {
            setIsLoading(false);
        }
    };

    // Auto-refresh if cache is older than 30 minutes. Show cached data
    // immediately as placeholder, then fetch fresh in background if stale.
    useEffect(() => {
        if (!currentUser) return;
        if (!data) {
            fetchBreadthData();
        } else {
            const STALE_MS = 30 * 60 * 1000; // 30 minutes
            const age = lastUpdated ? Date.now() - lastUpdated.getTime() : Infinity;
            if (age > STALE_MS) fetchBreadthData();
        }
    }, [currentUser]);

    // Helpers
    const getVixColor = (regime: string) => {
        if (regime?.includes("LOW")) return "text-amber-500";
        if (regime?.includes("NORMAL")) return "text-emerald-500";
        if (regime?.includes("ELEVATED")) return "text-amber-500";
        return "text-red-500";
    };

    const getVixBg = (regime: string) => {
        if (regime?.includes("LOW")) return "bg-amber-500";
        if (regime?.includes("NORMAL")) return "bg-emerald-500";
        if (regime?.includes("ELEVATED")) return "bg-amber-500";
        return "bg-red-500";
    };

    const getTrendColor = (trend: string) => {
        if (trend?.includes("STRONG UP")) return "text-emerald-500";
        if (trend?.includes("UP")) return "text-emerald-400";
        if (trend?.includes("RECOVERY")) return "text-amber-400";
        return "text-red-500";
    };

    if (isLoading && !data) {
        return <div className="flex h-full items-center justify-center text-slate-400 animate-pulse">Loading Market Breadth...</div>;
    }

    if (!data) return (
        <div className="flex h-full items-center justify-center flex-col gap-4 text-slate-400">
            <p>No market breadth data loaded yet.</p>
            <button onClick={() => fetchBreadthData()} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700">
                Load Market Breadth
            </button>
        </div>
    );

    const vix = data.indicators?.vix;
    const vvix = data.indicators?.vvix;
    const sp500 = data.indicators?.sp500;
    const nymo = data.indicators?.nymo;
    const tnx = data.indicators?.tnx;
    const btc = data.indicators?.btc;

    return (
        <div className="flex h-full bg-slate-50 overflow-hidden font-sans">

            {/* LEFT: SUMMARY PANEL */}
            <div className="w-96 bg-white border-r border-slate-200 flex flex-col p-6 overflow-y-auto">

                {/* HEADER */}
                <div className="mb-6 shrink-0 flex justify-between items-start">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
                            <IconActivity className="text-blue-600" /> Market Breadth
                        </h2>
                        <div className="flex items-center gap-2">
                            <p className="text-xs text-slate-500">VIX, VVIX & Technical Analysis</p>
                            {lastUpdated && (
                                <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                                    {lastUpdated.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </span>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() => fetchBreadthData()}
                        disabled={isLoading}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border transition-colors ${
                            isLoading
                                ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                        title="Refresh data from server"
                    >
                        <IconRefresh size={13} className={isLoading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>

                {/* VIX CARD */}
                {vix && (
                    <div className="bg-slate-900 rounded-xl p-6 shadow-xl mb-4 relative border border-slate-700 shrink-0">
                        <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                            <IconAlertTriangle size={60} color="white" />
                        </div>

                        <div className="relative z-10 flex flex-col gap-3">
                            <div>
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">VIX (Fear Index)</div>
                                <div className="flex items-baseline gap-2">
                                    <span className={`text-4xl font-bold ${getVixColor(vix.regime)}`}>
                                        {vix.current}
                                    </span>
                                    <span className="text-sm text-slate-400">{vix.percentile}th percentile</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 flex-wrap">
                                <div className={`px-2 py-1 rounded text-[10px] font-bold border uppercase tracking-wide ${
                                    vix.regime?.includes("HIGH") ? 'bg-red-900/50 border-red-700 text-red-400' :
                                    vix.regime?.includes("ELEVATED") ? 'bg-amber-900/50 border-amber-700 text-amber-400' :
                                    vix.regime?.includes("LOW") ? 'bg-amber-900/50 border-amber-700 text-amber-400' :
                                    'bg-emerald-900/50 border-emerald-700 text-emerald-400'
                                }`}>
                                    {vix.regime}
                                </div>
                                {vix.in_strong_zone && (
                                    <div className="px-2 py-1 rounded text-[10px] font-bold border bg-blue-900/50 border-blue-700 text-blue-400 uppercase tracking-wide">
                                        Rising Momentum
                                    </div>
                                )}
                            </div>

                            <div className="pt-3 border-t border-slate-700/50 grid grid-cols-4 gap-2 text-center">
                                <div>
                                    <div className="text-[9px] text-slate-500 uppercase">RSI</div>
                                    <div className={`text-sm font-mono ${
                                        vix.rsi > 70 ? 'text-red-400' : vix.rsi < 30 ? 'text-emerald-400' : 'text-slate-300'
                                    }`}>{vix.rsi}</div>
                                </div>
                                <div>
                                    <div className="text-[9px] text-slate-500 uppercase">Lower BB</div>
                                    <div className="text-sm font-mono text-slate-300">{vix.lower_band}</div>
                                </div>
                                <div>
                                    <div className="text-[9px] text-slate-500 uppercase">SMA 20</div>
                                    <div className="text-sm font-mono text-slate-300">{vix.sma20}</div>
                                </div>
                                <div>
                                    <div className="text-[9px] text-slate-500 uppercase">Upper BB</div>
                                    <div className="text-sm font-mono text-slate-300">{vix.upper_band}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* VVIX CARD */}
                {vvix && (
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 mb-4 shrink-0">
                        <div className="flex justify-between items-center mb-2">
                            <div>
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">VVIX (Vol of VIX)</div>
                                <div className="text-2xl font-bold text-slate-800">{vvix.current}</div>
                            </div>
                            <div className={`p-2 rounded-full ${vvix.current > 110 ? 'bg-amber-100' : 'bg-slate-100'}`}>
                                <IconTrendingUp size={20} className={vvix.current > 110 ? 'text-amber-600' : 'text-slate-400'} />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm mb-2">
                            <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                <span className="text-slate-500">RSI</span>
                                <span className={`font-mono font-bold ${
                                    vvix.rsi > 70 ? 'text-red-600' : vvix.rsi < 30 ? 'text-emerald-600' : 'text-slate-700'
                                }`}>{vvix.rsi}</span>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                <span className="text-slate-500">SMA 20</span>
                                <span className="font-mono font-bold text-slate-700">{vvix.sma20}</span>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                <span className="text-slate-500">Below SMA</span>
                                {vvix.below_sma ?
                                    <span className="text-emerald-600 font-bold">Yes</span> :
                                    <span className="text-amber-600 font-bold">No</span>
                                }
                            </div>
                        </div>
                        <p className="text-xs text-slate-500">{vvix.description}</p>
                    </div>
                )}

                {/* S&P 500 CARD */}
                {sp500 && (
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 mb-4 shrink-0">
                        <div className="flex justify-between items-center mb-3">
                            <div>
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">S&P 500</div>
                                <div className="text-2xl font-bold text-slate-800">{sp500.current?.toLocaleString()}</div>
                            </div>
                            <div className="flex flex-col gap-1 items-end">
                                <div className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                                    sp500.trend?.includes("STRONG UP") ? 'bg-emerald-100 text-emerald-700' :
                                    sp500.trend?.includes("UP") ? 'bg-emerald-50 text-emerald-600' :
                                    sp500.trend?.includes("RECOVERY") ? 'bg-amber-50 text-amber-600' :
                                    'bg-red-50 text-red-600'
                                }`}>
                                    {sp500.trend}
                                </div>
                                {sp500.volume_signal && (
                                    <div className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                                        sp500.volume_signal === 'HIGH' ? 'bg-blue-100 text-blue-700' :
                                        sp500.volume_signal?.includes('LOW') ? 'bg-amber-100 text-amber-700' :
                                        'bg-slate-100 text-slate-600'
                                    }`}>
                                        Vol: {sp500.volume_signal}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 text-sm mb-2">
                            <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                <span className="text-slate-500">RSI (14)</span>
                                <span className={`font-mono font-bold ${
                                    sp500.rsi > 70 ? 'text-red-600' : sp500.rsi < 30 ? 'text-emerald-600' : 'text-slate-700'
                                }`}>{sp500.rsi}</span>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                <span className="text-slate-500">BB Pos</span>
                                <span className="font-mono font-bold text-slate-700">{sp500.bb_position}%</span>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                <span className="text-slate-500">Vol Ratio</span>
                                <span className={`font-mono font-bold ${
                                    sp500.volume_ratio > 1.2 ? 'text-blue-600' :
                                    sp500.volume_ratio < 0.8 ? 'text-amber-600' : 'text-slate-700'
                                }`}>{sp500.volume_ratio ? `${sp500.volume_ratio.toFixed(2)}x` : 'N/A'}</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                <span className="text-slate-500">Above 50 MA</span>
                                {sp500.above_50ma ?
                                    <IconArrowUp size={16} className="text-emerald-600" /> :
                                    <IconArrowDown size={16} className="text-red-600" />
                                }
                            </div>
                            <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                <span className="text-slate-500">Above 200 MA</span>
                                {sp500.above_200ma ?
                                    <IconArrowUp size={16} className="text-emerald-600" /> :
                                    <IconArrowDown size={16} className="text-red-600" />
                                }
                            </div>
                        </div>

                        {sp500.sma50 && (
                            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2 text-xs">
                                <div className="text-slate-500">SMA 50: <span className="font-mono text-slate-700">{sp500.sma50?.toLocaleString()}</span></div>
                                {sp500.sma200 && <div className="text-slate-500">SMA 200: <span className="font-mono text-slate-700">{sp500.sma200?.toLocaleString()}</span></div>}
                            </div>
                        )}
                    </div>
                )}

                {/* NYMO CARD - McClellan Oscillator */}
                {nymo && (
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 mb-4 shrink-0">
                        <div className="flex justify-between items-center mb-2">
                            <div>
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">NYMO (McClellan Oscillator)</div>
                                <div className="flex items-baseline gap-2">
                                    <span className={`text-2xl font-bold ${
                                        nymo.current > 60 ? 'text-red-600' :
                                        nymo.current < -60 ? 'text-emerald-600' : 'text-slate-800'
                                    }`}>{nymo.current?.toFixed(1)}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                        nymo.zone === 'OVERBOUGHT' ? 'bg-red-100 text-red-700' :
                                        nymo.zone === 'OVERSOLD' ? 'bg-emerald-100 text-emerald-700' :
                                        'bg-slate-100 text-slate-600'
                                    }`}>{nymo.zone}</span>
                                </div>
                            </div>
                            <IconChartBar size={24} className="text-slate-300" />
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                            <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                <span className="text-slate-500">RSI</span>
                                <span className={`font-mono font-bold ${
                                    nymo.rsi > 70 ? 'text-red-600' : nymo.rsi < 30 ? 'text-emerald-600' : 'text-slate-700'
                                }`}>{nymo.rsi}</span>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                <span className="text-slate-500">SMA 20</span>
                                <span className="font-mono font-bold text-slate-700">{nymo.sma20?.toFixed(1)}</span>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                <span className="text-slate-500">SMA 50</span>
                                <span className="font-mono font-bold text-slate-700">{nymo.sma50?.toFixed(1)}</span>
                            </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">Market breadth oscillator. Extremes (&gt;60 or &lt;-60) signal potential reversals.</p>
                    </div>
                )}

                {/* TNX CARD - 10Y Treasury Yield */}
                {tnx && (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 shadow-sm border border-blue-200 mb-4 shrink-0">
                        <div className="flex justify-between items-center mb-2">
                            <div>
                                <div className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">TNX (10Y Treasury Yield)</div>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-2xl font-bold text-slate-800">{tnx.current?.toFixed(2)}%</span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${
                                        tnx.level === 'HIGH' ? 'bg-red-100 text-red-700' :
                                        tnx.level === 'ELEVATED' ? 'bg-amber-100 text-amber-700' :
                                        tnx.level === 'LOW' ? 'bg-emerald-100 text-emerald-700' :
                                        'bg-blue-100 text-blue-700'
                                    }`}>{tnx.level}</span>
                                </div>
                            </div>
                        </div>
                        {(tnx.sma20 || tnx.sma50) && (
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="flex items-center justify-between p-2 bg-white/60 rounded">
                                    <span className="text-slate-500">SMA 20</span>
                                    <span className="font-mono font-bold text-slate-700">{tnx.sma20 ? `${tnx.sma20.toFixed(2)}%` : 'N/A'}</span>
                                </div>
                                <div className="flex items-center justify-between p-2 bg-white/60 rounded">
                                    <span className="text-slate-500">SMA 50</span>
                                    <span className="font-mono font-bold text-slate-700">{tnx.sma50 ? `${tnx.sma50.toFixed(2)}%` : 'N/A'}</span>
                                </div>
                            </div>
                        )}
                        <p className="text-xs text-slate-500 mt-2">Bond market sentiment indicator. Rising yields = tighter conditions.</p>
                    </div>
                )}

                {/* BTC CARD */}
                {btc && (
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 shadow-sm border border-orange-200 mb-4 shrink-0">
                        <div className="flex justify-between items-center mb-2">
                            <div>
                                <div className="text-[10px] font-bold text-orange-600 uppercase tracking-wider">BTC (Bitcoin)</div>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-2xl font-bold text-slate-800">${btc.current?.toLocaleString()}</span>
                                    {btc.above_50ma !== undefined && (
                                        <span className={`text-xs px-2 py-0.5 rounded ${
                                            btc.above_50ma ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                                        }`}>
                                            {btc.above_50ma ? 'Above' : 'Below'} 50 MA
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-white/60 rounded text-sm">
                            <span className="text-slate-500">SMA 50</span>
                            <span className="font-mono font-bold text-slate-700">${btc.sma50?.toLocaleString()}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">Risk appetite indicator. BTC often leads risk-on/risk-off sentiment.</p>
                    </div>
                )}

                {/* SIGNALS PANEL */}
                {data.signals && data.signals.length > 0 && (
                    <div className="shrink-0">
                        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Signals</h3>
                        <div className="space-y-2">
                            {data.signals.map((signal: string, idx: number) => (
                                <div key={idx} className={`p-3 rounded-lg text-sm ${
                                    signal.includes("extreme fear") || signal.includes("oversold") ?
                                        'bg-red-50 border border-red-200 text-red-700' :
                                    signal.includes("complacency") || signal.includes("overbought") ?
                                        'bg-amber-50 border border-amber-200 text-amber-700' :
                                    signal.includes("No extreme") ?
                                        'bg-slate-50 border border-slate-200 text-slate-600' :
                                        'bg-blue-50 border border-blue-200 text-blue-700'
                                }`}>
                                    <IconAlertTriangle size={14} className="inline mr-2" />
                                    {signal}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* RIGHT: CHARTS */}
            <div className="flex-1 p-6 flex flex-col gap-4 overflow-hidden min-w-0">

                {/* Date Range Selector */}
                <div className="flex justify-end gap-2 shrink-0">
                    {[30, 90, 180, 365].map(d => (
                        <button
                            key={d}
                            onClick={() => { setDays(d); fetchBreadthData(d); }}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                                days === d ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            {d}d
                        </button>
                    ))}
                </div>

                {/* VIX Chart with Bollinger Bands */}
                {vix?.time_series && (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col p-4">
                        <div className="flex justify-between items-center mb-4 shrink-0">
                            <div>
                                <h3 className="text-lg font-bold text-slate-800">VIX with Bollinger Bands</h3>
                                <p className="text-xs text-slate-500">20-day SMA with 2 standard deviation bands</p>
                            </div>
                        </div>

                        <div className="flex-1">
                            <ResponsiveContainer width="100%" height={220}>
                                <ComposedChart data={vix.time_series} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                                    <defs>
                                        <linearGradient id="bandFill" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.1}/>
                                            <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.05}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis
                                        dataKey="date"
                                        tick={{fontSize: 10, fill: '#64748b'}}
                                        tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis
                                        tick={{fontSize: 10, fill: '#64748b'}}
                                        domain={['auto', 'auto']}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#1e293b',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontSize: '11px',
                                            color: '#fff'
                                        }}
                                        labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', {
                                            year: 'numeric', month: 'short', day: 'numeric'
                                        })}
                                    />

                                    {/* Bollinger Bands */}
                                    <Area
                                        type="monotone"
                                        dataKey="upper_band"
                                        stroke="#94a3b8"
                                        strokeDasharray="3 3"
                                        fill="none"
                                        name="Upper Band"
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="lower_band"
                                        stroke="#94a3b8"
                                        strokeDasharray="3 3"
                                        fill="none"
                                        name="Lower Band"
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="sma20"
                                        stroke="#3b82f6"
                                        strokeWidth={1}
                                        dot={false}
                                        name="SMA 20"
                                    />

                                    {/* VIX Line */}
                                    <Line
                                        type="monotone"
                                        dataKey="value"
                                        stroke="#ef4444"
                                        strokeWidth={2}
                                        dot={false}
                                        name="VIX"
                                    />

                                    {/* Reference lines */}
                                    <ReferenceLine y={20} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: '20', fill: '#f59e0b', fontSize: 10 }} />
                                    <ReferenceLine y={30} stroke="#ef4444" strokeDasharray="5 5" label={{ value: '30', fill: '#ef4444', fontSize: 10 }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* S&P 500 Chart with Bollinger Bands */}
                {sp500?.time_series && (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col p-4">
                        <div className="flex justify-between items-center mb-4 shrink-0">
                            <div>
                                <h3 className="text-lg font-bold text-slate-800">S&P 500 Technical Analysis</h3>
                                <p className="text-xs text-slate-500">Price with 50 MA and Bollinger Bands</p>
                            </div>
                        </div>

                        <div className="flex-1">
                            <ResponsiveContainer width="100%" height={220}>
                                <ComposedChart data={sp500.time_series} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis
                                        dataKey="date"
                                        tick={{fontSize: 10, fill: '#64748b'}}
                                        tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        interval="preserveStartEnd"
                                    />
                                    <YAxis
                                        tick={{fontSize: 10, fill: '#64748b'}}
                                        domain={['auto', 'auto']}
                                        tickFormatter={(val) => val.toLocaleString()}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#1e293b',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontSize: '11px',
                                            color: '#fff'
                                        }}
                                        labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', {
                                            year: 'numeric', month: 'short', day: 'numeric'
                                        })}
                                        formatter={(value: number) => [value.toLocaleString(), '']}
                                    />

                                    {/* Bollinger Bands */}
                                    <Area
                                        type="monotone"
                                        dataKey="upper_band"
                                        stroke="#94a3b8"
                                        strokeDasharray="3 3"
                                        fill="none"
                                        name="Upper Band"
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="lower_band"
                                        stroke="#94a3b8"
                                        strokeDasharray="3 3"
                                        fill="none"
                                        name="Lower Band"
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="sma50"
                                        stroke="#8b5cf6"
                                        strokeWidth={1}
                                        dot={false}
                                        name="SMA 50"
                                    />

                                    {/* Price Line */}
                                    <Line
                                        type="monotone"
                                        dataKey="close"
                                        stroke="#10b981"
                                        strokeWidth={2}
                                        dot={false}
                                        name="S&P 500"
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MarketBreadthView;
